/**
 * assetSchema.js — Logica pura per la gestione degli asset fisici.
 *
 * Funzioni pure (nessuna dipendenza da Vue/axios/DOM) estratte dalla parte
 * azienda di `src/pages/AdminAssets.vue` per essere riusate dal componente
 * `components/CompanyAssetsManager.vue` (Tab_GestioneAsset della Pagina_Moduli)
 * e testate in isolamento (consolidamento-gestione-asset-in-moduli).
 *
 * Contenuto:
 *   - parseCampiSchema(raw): normalizza il `campi_schema` di una tipologia asset
 *     in un array di campi `{name, type, label, required}`. Non solleva mai eccezioni.
 *   - buildCreatePayload(...): costruisce il body di `POST /assets` (VN2, R2.5).
 *   - buildUpdatePayload(...): costruisce il body di `PUT /assets/<id>` (VN2, R2.6).
 *   - shouldRedirectAdminAssets(tipologia): decide se la route `admin-assets`
 *     deve redirigere l'Admin_Azienda al Tab_GestioneAsset (soluzione B, R5.1/R5.3).
 *
 * Esportate con `module.exports` per compatibilità con l'ambiente Jest
 * CommonJS usato dalle suite esistenti in `GUI/roadme-gui/tests`.
 */

/**
 * parseCampiSchema — normalizza il `campi_schema` di una tipologia asset in un
 * array di campi. Riproduce fedelmente la logica odierna di `AdminAssets.vue`,
 * garantendo la robustezza (mai un throw):
 *   - array già pronto  → restituito invariato;
 *   - stringa JSON valida → parsata; se il risultato è array lo ritorna,
 *     altrimenti `[]`;
 *   - stringa JSON malformata → `[]`;
 *   - `null`/`undefined`/`""` (o qualunque altro valore) → `[]`.
 *
 * @param {*} raw valore grezzo del campi_schema
 * @returns {Array} array di campi (mai null/undefined, mai throw)
 */
function parseCampiSchema(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
}

/**
 * buildCreatePayload — costruisce il body per `POST /assets` (VN2, R2.5).
 * L'oggetto risultante ha ESATTAMENTE le chiavi attese dall'endpoint:
 *   { fk_company, fk_commessa, fk_asset_type, identificativo, dati }
 *
 * @param {Object} params
 * @param {*} params.fkCompany id azienda corrente
 * @param {*} params.fkCommessa id commessa selezionata
 * @param {*} params.fkAssetType id tipologia asset selezionata
 * @param {*} params.identificativo identificativo dell'asset
 * @param {Object} params.dati valori dei Campi_Dinamici
 * @returns {Object} body della richiesta di creazione
 */
function buildCreatePayload({ fkCompany, fkCommessa, fkAssetType, identificativo, dati }) {
  return {
    fk_company: fkCompany,
    fk_commessa: fkCommessa,
    fk_asset_type: fkAssetType,
    identificativo: identificativo,
    dati: dati
  };
}

/**
 * buildUpdatePayload — costruisce il body per `PUT /assets/<id>` (VN2, R2.6).
 * L'oggetto risultante ha ESATTAMENTE le chiavi attese dall'endpoint
 * (nessun `fk_asset_type`/`fk_commessa`, coerente con il contratto):
 *   { fk_company, identificativo, dati }
 *
 * @param {Object} params
 * @param {*} params.fkCompany id azienda corrente
 * @param {*} params.identificativo identificativo dell'asset
 * @param {Object} params.dati valori dei Campi_Dinamici
 * @returns {Object} body della richiesta di aggiornamento
 */
function buildUpdatePayload({ fkCompany, identificativo, dati }) {
  return {
    fk_company: fkCompany,
    identificativo: identificativo,
    dati: dati
  };
}

/**
 * shouldRedirectAdminAssets — decide se la route `admin-assets` deve redirigere
 * (soluzione B, R5.1/R5.3). Riflette la logica del `beforeEnter` del router:
 * redirige SOLO l'Admin_Azienda (`'B'`, ed eventualmente `'A'`); per il
 * Developer (`'D'`) e per ogni altro valore (incluso `null`/`undefined`/altro)
 * non redirige (comportamento conservativo).
 *
 * @param {*} tipologia tipologia utente (tipicamente stringa da localStorage)
 * @returns {boolean} true se e solo se tipologia è 'B' o 'A'
 */
function shouldRedirectAdminAssets(tipologia) {
  return tipologia === "B" || tipologia === "A";
}

module.exports = {
  parseCampiSchema,
  buildCreatePayload,
  buildUpdatePayload,
  shouldRedirectAdminAssets
};
